import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertTriangle, Target, Loader2, Settings, Shield, Users, FileText, Vote, Award, Calendar } from 'lucide-react';
import { calcularDiasRestantes, formatarData, formatarDataSimples } from '../utils/dateUtils';
import { useCronograma } from '../hooks/useCronograma';
import { useAuth } from '../context/AuthContext';
import { getSistemaInfo } from '../utils/sistemaInfo';
import { supabase } from '../config/supabase';

const DashboardTab: React.FC = () => {
  const { fases, loading, error, getEstatisticas, recriarCronograma } = useCronograma();
  const { user, empresaSelecionada, atualizarDataEmpresa, atualizarDataUsuario } = useAuth();
  const [dataTerminoMandato, setDataTerminoMandato] = useState(
    empresaSelecionada?.data_termino_mandato || user?.data_termino_mandato || '2025-09-26'
  );
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Obter informações dinâmicas do sistema baseado no tipo de comissão
  const cnaeEmpresa = (empresaSelecionada as any)?.cnae_principal;
  const tipoComissaoBanco = (empresaSelecionada as any)?.tipo_comissao;
  const nomeEmpresa = (empresaSelecionada as any)?.nome;
  
  const [sistemaInfo, setSistemaInfo] = useState({
    nome: 'CIPA FÁCIL',
    nomeCompleto: 'CIPA FÁCIL EHS',
    descricao: 'Sistema Completo de Gestão CIPA - 100% Conforme NR-05',
    descricaoCurta: 'Sistema 100% Conforme NR-05 Vigente',
    tipo: 'CIPA' as 'CIPA' | 'CIPAMIN' | 'CIPATR',
    norma: 'NR-05'
  });

  useEffect(() => {
    getSistemaInfo(cnaeEmpresa, tipoComissaoBanco, nomeEmpresa).then(setSistemaInfo);
  }, [cnaeEmpresa, tipoComissaoBanco, nomeEmpresa]);

  // Função para obter o ícone correto
  const getIconComponent = (icon: any) => {
    if (typeof icon === 'string') {
      switch (icon) {
        case 'Users': return Users;
        case 'FileText': return FileText;
        case 'Vote': return Vote;
        case 'CheckCircle': return CheckCircle;
        case 'Award': return Award;
        case 'Calendar': return Calendar;
        default: return Calendar;
      }
    }
    return icon || Calendar;
  };

  // Função para atualizar a data de término do mandato
  const atualizarDataTermino = async () => {
    if (!user) return;
    
    // Verificar se a data é a mesma que já está salva
    const dataAtual = empresaSelecionada?.data_termino_mandato || user?.data_termino_mandato;
    if (dataAtual === dataTerminoMandato) {
      alert('A data informada é a mesma que já está configurada no sistema. Nenhuma alteração foi necessária.');
      return;
    }
    
    try {
      setUpdating(true);
      console.log('💾 Salvando data na empresa:', empresaSelecionada?.nome, 'data:', dataTerminoMandato);
      
      // Se há empresa selecionada, salvar na empresa
      if (empresaSelecionada) {
        console.log('💾 Atualizando empresa ID:', empresaSelecionada.id);
        const { error } = await supabase
          .from('cp_empresas')
          .update({ 
            data_termino_mandato: dataTerminoMandato,
            updated_at: new Date().toISOString()
          })
          .eq('id', empresaSelecionada.id);

        if (error) {
          console.error('❌ Erro ao atualizar data da empresa:', error);
          alert('Erro ao atualizar data de término do mandato');
          return;
        }
        console.log('✅ Data salva com sucesso na empresa');
        // Atualizar a empresa selecionada com a nova data
        atualizarDataEmpresa(empresaSelecionada.id, dataTerminoMandato);
      } else {
        console.log('💾 Atualizando usuário ID:', user.id);
        // Fallback: salvar no usuário (para compatibilidade)
        const { error } = await supabase
          .from('cp_usuarios')
          .update({ 
            data_termino_mandato: dataTerminoMandato,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (error) {
          console.error('❌ Erro ao atualizar data do usuário:', error);
          alert('Erro ao atualizar data de término do mandato');
          return;
        }
        console.log('✅ Data salva com sucesso no usuário');
        // Atualizar a data do usuário no contexto
        atualizarDataUsuario(dataTerminoMandato);
      }

      // Recriar o cronograma com as novas datas
      console.log('🔄 Recriando cronograma...');
      await recriarCronograma(dataTerminoMandato);
      
      alert('Data de término do mandato atualizada com sucesso!');
    } catch (err) {
      console.error('❌ Erro ao atualizar data:', err);
      alert('Erro ao atualizar data de término do mandato');
    } finally {
      setUpdating(false);
    }
  };

  // Atualizar data quando empresa selecionada mudar
  useEffect(() => {
    console.log('🔄 useEffect DashboardTab - empresaSelecionada:', empresaSelecionada?.nome, 'data:', empresaSelecionada?.data_termino_mandato);
    console.log('🔄 useEffect DashboardTab - user data:', user?.data_termino_mandato);
    
    if (empresaSelecionada?.data_termino_mandato) {
      console.log('✅ Carregando data da empresa:', empresaSelecionada.data_termino_mandato);
      setDataTerminoMandato(empresaSelecionada.data_termino_mandato);
    } else if (user?.data_termino_mandato) {
      console.log('✅ Carregando data do usuário:', user.data_termino_mandato);
      setDataTerminoMandato(user.data_termino_mandato);
    }
  }, [empresaSelecionada, user]);

  // Análise de conformidade NR-05
  const analiseConformidade = [
    {
      item: 'Formação da Comissão Eleitoral',
      exigencia: 'Mínimo 60 dias antes',
      implementado: '60 dias antes (conforme NR-05)',
      status: 'conforme',
      artigo: 'Art. 5.5.2'
    },
    {
      item: 'Comunicação ao Sindicato',
      exigencia: 'Com antecedência',
      implementado: '58 dias antes (após formação da comissão)',
      status: 'conforme',
      artigo: 'Art. 5.5.1.1'
    },
    {
      item: 'Publicação do Edital',
      exigencia: 'Abertura de prazos para inscrições',
      implementado: '45 dias antes',
      status: 'conforme',
      artigo: 'Art. 5.5.3(a)'
    },
    {
      item: 'Período de Inscrições',
      exigencia: 'Mínimo 15 dias corridos',
      implementado: '15 dias corridos (44 a 29 dias antes)',
      status: 'conforme',
      artigo: 'Art. 5.5.3(b)'
    },
    {
      item: 'Publicação da Lista de Candidatos',
      exigencia: 'Após término das inscrições',
      implementado: '28 dias antes (1 dia após inscrições)',
      status: 'conforme',
      artigo: 'Art. 5.5.3(e)'
    },
    {
      item: 'Eleição',
      exigencia: 'Mínimo 30 dias antes',
      implementado: '31 dias antes (conforme NR-05)',
      status: 'conforme',
      artigo: 'Art. 5.5.3(f)'
    },
    {
      item: 'Apuração dos Votos',
      exigencia: 'Em horário normal de trabalho',
      implementado: '30 dias antes (1 dia após eleição)',
      status: 'conforme',
      artigo: 'Art. 5.5.3(i)'
    },
    {
      item: 'Treinamento Obrigatório',
      exigencia: 'Antes da posse',
      implementado: '15 dias antes (antes da posse)',
      status: 'conforme',
      artigo: 'Art. 5.7.1'
    },
    {
      item: 'Posse dos Membros',
      exigencia: 'Primeiro dia útil após término',
      implementado: '1 dia após término do mandato',
      status: 'conforme',
      artigo: 'Art. 5.4.7'
    }
  ];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <p className="text-red-700">Erro ao carregar dados: {error}</p>
      </div>
    );
  }

  const stats = getEstatisticas();
  
  return (
  <div className="space-y-6">
    <div className={`bg-gradient-to-r ${sistemaInfo.tipo === 'CIPAMIN' ? 'from-orange-600 to-red-600' : 'from-blue-600 to-purple-600'} text-white rounded-xl p-6`}>
      <h1 className="text-3xl font-bold mb-2">{sistemaInfo.nome}</h1>
      <p className={`${sistemaInfo.tipo === 'CIPAMIN' ? 'text-orange-100' : 'text-blue-100'}`}>{sistemaInfo.descricao}</p>
      <div className="mt-4 p-3 bg-white bg-opacity-20 rounded-lg">
        <p className="text-sm">
          <strong>Data de Término do Mandato Atual:</strong> {dataTerminoMandato.split('-').reverse().join('/')}
        </p>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-green-500">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Fases Concluídas</p>
            <p className="text-2xl font-bold text-green-600">{stats.concluidas}</p>
          </div>
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
      </div>
      <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-orange-500">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Próximo Vencimento</p>
            <p className="text-2xl font-bold text-orange-600">{stats.proximoVencimento}</p>
          </div>
          <Clock className="w-8 h-8 text-orange-500" />
        </div>
      </div>
      <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-red-500">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Fases Vencidas</p>
            <p className="text-2xl font-bold text-red-600">{stats.vencidas}</p>
          </div>
          <AlertTriangle className="w-8 h-8 text-red-500" />
        </div>
      </div>
      <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-blue-500">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Total de Fases</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
          </div>
          <Target className="w-8 h-8 text-blue-500" />
        </div>
      </div>
    </div>

    {/* CONFIGURAÇÃO DO MANDATO */}
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Settings className="w-5 h-5 text-blue-600" />
        Configuração do Mandato
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block font-medium text-gray-700 mb-2">
            Data de Término do Mandato Atual
          </label>
          <input
            type="date"
            value={dataTerminoMandato}
            onChange={(e) => setDataTerminoMandato(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-end gap-2">
          <button
            onClick={atualizarDataTermino}
            disabled={updating}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {updating ? 'Atualizando...' : 'Atualizar'}
          </button>
          <button
            onClick={() => setShowAnalysis(!showAnalysis)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            Análise de Conformidade
          </button>
        </div>
      </div>
    </div>

    {/* ANÁLISE DE CONFORMIDADE */}
    {showAnalysis && (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-600" />
          Análise de Conformidade NR-05
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Item</th>
                <th className="text-left py-2">Exigência NR-05</th>
                <th className="text-left py-2">Implementado</th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Artigo</th>
              </tr>
            </thead>
            <tbody>
              {analiseConformidade.map((item, index) => (
                <tr key={index} className="border-b">
                  <td className="py-2 font-medium">{item.item}</td>
                  <td className="py-2 text-gray-600">{item.exigencia}</td>
                  <td className="py-2 text-green-600 font-medium">{item.implementado}</td>
                  <td className="py-2">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                      CONFORME
                    </span>
                  </td>
                  <td className="py-2 text-blue-600 text-sm">{item.artigo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-red-600" />
        Fases Vencidas
      </h3>
      <ul className="list-disc pl-5 space-y-2">
        {stats.fasesVencidas && stats.fasesVencidas.map((fase: any, index: number) => (
          <li key={index} className="text-sm text-gray-700">
            {fase.titulo} - {formatarData(fase.data)}
          </li>
        ))}
      </ul>
    </div>
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-orange-600" />
        Próximas ao Vencimento
      </h3>
      <ul className="list-disc pl-5 space-y-2">
        {fases.filter(fase => {
          const diasRestantes = calcularDiasRestantes(fase.data);
          return diasRestantes > 0 && diasRestantes <= 7;
        }).map((fase: any, index: number) => (
          <li key={index} className="text-sm text-gray-700">
            {fase.titulo} - {formatarData(fase.data)}
          </li>
        ))}
      </ul>
    </div>

    {/* PRÓXIMAS AÇÕES CRÍTICAS */}
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-orange-600" />
        Próximas Ações Críticas
      </h3>
      <div className="space-y-3">
        {fases.slice(0, 3).map(fase => {
          const diasRestantes = calcularDiasRestantes(fase.data);
          const IconComponent = getIconComponent(fase.icon);
          return (
            <div key={fase.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`${fase.cor} p-2 rounded-lg`}>
                  <IconComponent className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium">{fase.titulo}</p>
                  <p className="text-sm text-gray-600">{formatarDataSimples(fase.data)}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                diasRestantes < 0 
                  ? 'bg-red-100 text-red-800' 
                  : diasRestantes <= 7 
                  ? 'bg-orange-100 text-orange-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {diasRestantes > 0 ? `${diasRestantes} dias` : 'Vencido'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </div>
  );
};

export default DashboardTab;
